import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Shield, Key, Database, ListChecks, Plus } from "lucide-react";

const TABS = [
  { id: "audit", label: "Audit Log" },
  { id: "licenses", label: "Licenses" },
  { id: "reviews", label: "Access Reviews" },
  { id: "backups", label: "Backups" },
];

export default function IT() {
  const [tab, setTab] = useState("audit");
  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="it-page">
      <PageHeader title="IT & Compliance" subtitle="Audit logs, software licence tracker, access reviews and backup snapshots." />
      <div className="mt-6 flex flex-wrap gap-1 border-b border-[#DEE2E6]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`it-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.id ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>{t.label}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "audit" && <AuditTab />}
        {tab === "licenses" && <LicensesTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "backups" && <BackupsTab />}
      </div>
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  useEffect(() => { Promise.all([
    api.get("/it/audit-log").then(({ data }) => setRows(data)),
    api.get("/it/audit-log/summary").then(({ data }) => setSummary(data)),
  ]); }, []);
  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="audit-summary">
          <Kpi label={`Events last ${summary.window_days}d`} value={summary.total} />
          <Kpi label="POSTs" value={summary.by_action?.POST || 0} />
          <Kpi label="PATCHes" value={summary.by_action?.PATCH || 0} />
          <Kpi label="Active users" value={Object.keys(summary.by_user || {}).length} />
        </div>
      )}
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>When</Th><Th>User</Th><Th>Action</Th><Th>Path</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 100).map((a, i) => (
              <tr key={a.id} className="border-b border-[#DEE2E6]" data-testid={`audit-row-${i}`}>
                <Td className="text-xs text-[#495057]">{new Date(a.at).toLocaleString()}</Td>
                <Td className="text-xs">{a.user_email}</Td>
                <Td><span className="text-[10px] uppercase tracking-[0.15em] border border-[#0A0A0A] px-2 py-0.5">{a.action}</span></Td>
                <Td className="font-mono text-xs">{a.entity}</Td>
                <Td className="tabular-nums">{a.meta?.status}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#495057]">No audit events yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function LicensesTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/it/licenses").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex justify-end mb-4"><button onClick={() => setOpen(true)} data-testid="add-license-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Plus size={14}/> New licence</button></div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="licenses-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]"><tr><Th>Vendor</Th><Th>Product</Th><Th align="right">Seats</Th><Th align="right">Cost/yr</Th><Th>Renews</Th><Th align="right">Days left</Th><Th>Status</Th></tr></thead>
          <tbody>
            {rows.map((li, i) => (
              <tr key={li.id} className="border-b border-[#DEE2E6]" data-testid={`license-row-${i}`}>
                <Td>{li.vendor}</Td><Td><div className="font-medium">{li.product}</div></Td>
                <Td align="right" className="tabular-nums">{li.seats}</Td>
                <Td align="right" className="tabular-nums">{li.cost_per_year.toLocaleString()}</Td>
                <Td>{li.renewal_date}</Td>
                <Td align="right" className={`tabular-nums ${li.days_to_renewal < 30 ? "text-[#D90429]" : ""}`}>{li.days_to_renewal}</Td>
                <Td><span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${li.renewal_status === "expired" ? "border-[#D90429] text-[#D90429]" : li.renewal_status === "renew_soon" ? "border-[#F5B700] text-[#F5B700]" : "border-[#0B7A75] text-[#0B7A75]"}`}>{li.renewal_status}</span></Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#495057]">No licences.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <LicenseForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function LicenseForm({ onClose, onSaved }) {
  const [f, setF] = useState({ vendor: "", product: "", seats: 1, cost_per_year: 0, renewal_date: "", notes: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/it/licenses", { ...f, seats: parseInt(f.seats), cost_per_year: parseFloat(f.cost_per_year) }); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New software licence" onClose={onClose} testid="license-modal">
      <form onSubmit={save} className="space-y-4" data-testid="license-form">
        <Field label="Vendor" required value={f.vendor} onChange={(v) => setF({ ...f, vendor: v })} testid="li-vendor" />
        <Field label="Product" required value={f.product} onChange={(v) => setF({ ...f, product: v })} testid="li-product" />
        <div className="grid grid-cols-2 gap-3"><Field label="Seats" type="number" value={f.seats} onChange={(v) => setF({ ...f, seats: v })} testid="li-seats" /><Field label="Cost/year" type="number" value={f.cost_per_year} onChange={(v) => setF({ ...f, cost_per_year: v })} testid="li-cost" /></div>
        <Field label="Renewal date" required value={f.renewal_date} onChange={(v) => setF({ ...f, renewal_date: v })} testid="li-renew" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="li-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button></div>
      </form>
    </Modal>
  );
}

function ReviewsTab() {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("");
  const load = () => api.get("/it/access-reviews").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const create = async () => { if (!period) return; await api.post("/it/access-reviews", { period }); setPeriod(""); load(); };
  const complete = async (rid) => { await api.post(`/it/access-reviews/${rid}/complete`); load(); };
  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <Field label="Period (e.g. 2026-Q1)" value={period} onChange={setPeriod} testid="review-period" />
        <button onClick={create} disabled={!period} data-testid="create-review-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm disabled:opacity-50"><ListChecks size={14}/> New access review</button>
      </div>
      <div className="space-y-3" data-testid="reviews-list">
        {rows.map((rv) => (
          <div key={rv.id} className="border border-[#0A0A0A] bg-white p-4" data-testid={`review-${rv.id}`}>
            <div className="flex justify-between items-start">
              <div><div className="font-bold">{rv.period}</div><div className="text-xs text-[#495057]">{rv.user_count} users snapshotted on {new Date(rv.created_at).toLocaleDateString()}</div></div>
              <div className="flex items-center gap-2"><span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${rv.status === "complete" ? "border-[#0B7A75] text-[#0B7A75]" : "border-[#F5B700] text-[#F5B700]"}`}>{rv.status}</span>{rv.status !== "complete" && <button onClick={() => complete(rv.id)} data-testid={`complete-${rv.id}`} className="px-2 py-1 text-xs bg-[#0A0A0A] text-white">Mark complete</button>}</div>
            </div>
            <div className="mt-3 max-h-40 overflow-y-auto text-xs border-t border-[#DEE2E6] pt-2">
              {rv.snapshot.map((u, i) => (<div key={i} className="flex justify-between py-0.5"><span className="font-mono">{u.email}</span><span className="text-[#495057]">{u.role} · {u.provider}{u.revoked && " · REVOKED"}</span></div>))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-[#495057] border border-[#DEE2E6]">No access reviews.</div>}
      </div>
    </>
  );
}

function BackupsTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/it/backups").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const trigger = async () => { await api.post("/it/backups"); load(); };
  return (
    <>
      <div className="flex justify-end mb-4"><button onClick={trigger} data-testid="trigger-backup-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Database size={14}/> Trigger backup snapshot</button></div>
      <div className="space-y-3" data-testid="backups-list">
        {rows.map((b) => (
          <div key={b.id} className="border border-[#0A0A0A] bg-white p-4 text-sm" data-testid={`backup-${b.id}`}>
            <div className="flex justify-between"><div className="font-mono text-xs">{b.id.slice(0, 12)}</div><div className="text-xs text-[#495057]">{new Date(b.created_at).toLocaleString()}</div></div>
            <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
              {Object.entries(b.manifest).map(([k, v]) => (<div key={k} className="border border-[#DEE2E6] px-2 py-1"><div className="text-[#495057]">{k}</div><div className="font-bold tabular-nums">{v}</div></div>))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-[#495057] border border-[#DEE2E6]">No backups recorded.</div>}
      </div>
    </>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "good" ? "border-[#0B7A75]" : tone === "warn" ? "border-[#F5B700]" : tone === "bad" ? "border-[#D90429]" : "border-[#DEE2E6]";
  return (<div className={`border bg-white p-4 ${cls}`}><div className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{label}</div><div className="mt-1 text-xl font-bold tabular-nums">{value}</div></div>);
}
